/**
 * NmethInfoPage
 * 新方法論資訊（A6.4-FORM-METH-002 標題頁）
 * 單一表單，4 個欄位，依 selectedProposalId 載入
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nmethInfoService, { NmethInfoDetail, NmethInfoUpdate } from '../services/nmethInfoService';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import DateInput from '../components/DateInput';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

const NmethInfoPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId, selectedProposal } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NmethInfoDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standardTypes, setStandardTypes] = useState<SystemCode[]>([]);

  // 表單狀態
  const [formData, setFormData] = useState({
    type_of_standard: null as number | null,
    reference_number: '',
    received_date: '',
    posting_date: '',
  });

  // 初始化
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NmethInfoPage');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // 載入代碼
  useEffect(() => {
    getSystemCodesByType('A6.4_nmeth_standard_type')
      .then(setStandardTypes)
      .catch(() => setStandardTypes([]));
  }, []);

  // 載入資料
  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setFormData({ type_of_standard: null, reference_number: '', received_date: '', posting_date: '' });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await nmethInfoService.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setFormData({
            type_of_standard: result.type_of_standard,
            reference_number: result.reference_number || '',
            received_date: result.received_date || '',
            posting_date: result.posting_date || '',
          });
        } else {
          setData(null);
          // 帶入 proposal 的 type_of_standard 預設值
          setFormData({
            type_of_standard: selectedProposal?.type_of_standard || null,
            reference_number: '',
            received_date: '',
            posting_date: '',
          });
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setData(null);
          setFormData({
            type_of_standard: selectedProposal?.type_of_standard || null,
            reference_number: '',
            received_date: '',
            posting_date: '',
          });
        } else {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProposalId, selectedProposal, isZh]);

  // 儲存
  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const updateData: NmethInfoUpdate = {
        type_of_standard: formData.type_of_standard,
        reference_number: formData.reference_number || undefined,
        received_date: formData.received_date || undefined,
        posting_date: formData.posting_date || undefined,
      };
      const result = await nmethInfoService.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  // 權限
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }
  if (!hasPermission('nmeth_info', 'read')) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  }
  const canUpdate = hasPermission('nmeth_info', 'update');

  const fieldStyle: React.CSSProperties = { marginBottom: '20px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nmeth_info" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div style={{
          width: '100%',
          background: '#fff',
          borderRadius: '10px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e8e8e8',
          padding: '28px 32px',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '6px' }}>
            {isZh ? '由秘書處及方法論專家小組填寫' : 'Information to be completed by the secretariat and Methodologies Expert Panel'}
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '24px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
            {isZh ? 'Information to be completed by the secretariat and Methodologies Expert Panel' : 'To be completed by the UNFCCC secretariat and MEP'}
          </div>

          {/* 標準類型 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '標準類型' : 'Type of Standard'}</label>
            <select style={inputStyle} value={formData.type_of_standard || ''} disabled={!canUpdate}
              onChange={(e) => setFormData({ ...formData, type_of_standard: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{isZh ? '請選擇' : 'Choose an item...'}</option>
              {standardTypes.map(s => (
                <option key={s.id} value={s.id}>{isZh ? (s.code_cname || s.code_ename) : (s.code_ename || s.code_cname)}</option>
              ))}
            </select>
            <small style={{ color: '#999', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {isZh ? '預設從方法論提案帶入' : 'Default from methodology proposal'}
            </small>
          </div>

          {/* 方法論編號與名稱 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '方法論編號與名稱' : 'Unique Reference Number and Title'}</label>
            <input style={inputStyle} value={formData.reference_number} disabled={!canUpdate}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              placeholder={isZh ? '方法論的唯一參考編號與名稱' : 'Unique reference number and title of the proposed new methodology'} />
          </div>

          {/* 秘書處收件日期 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '秘書處收件日期' : 'Date Received at UNFCCC Secretariat'}</label>
            <DateInput value={formData.received_date} disabled={!canUpdate}
              onChange={(v) => setFormData({ ...formData, received_date: v })} />
          </div>

          {/* 公開諮詢張貼日期 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '公開諮詢張貼日期' : 'Date of Posting for Global Stakeholder Consultation'}</label>
            <DateInput value={formData.posting_date} disabled={!canUpdate}
              onChange={(v) => setFormData({ ...formData, posting_date: v })} />
          </div>

          {/* 儲存按鈕 */}
          {canUpdate && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '28px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              <button className="btn-primary" onClick={handleSave} disabled={loading}
                style={{ padding: '8px 32px', fontSize: '15px' }}>
                {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NmethInfoPage;
